import { create } from 'zustand';
import { IPosition } from '../types/common.types';
import { GameplayState, GameState } from '../types/store.types';
import { DungeonRunConfig } from '../types/dungeon.types';

export type { GameState, GameplayState };

interface GameActions {
  setCameraPosition: (pos: IPosition) => void;
  setScreenWidth: (w: number) => void;
  setScreenHeight: (h: number) => void;
  setZoomFactor: (z: number) => void;
  setPlayerPosition: (pos: IPosition) => void;
  setLockCameraPosition: (v: boolean) => void;
  setGameplay: (v: GameplayState) => void;
  setCurrentDelta: (d: number) => void;
  setIsPaused: (v: boolean) => void;
  toggleIsPaused: () => void;
  setIsCapturingInput: (v: boolean) => void;
  setCurrentFloor: (f: number) => void;
  /**
   * Pure state setter for the active in-dungeon interface — does NOT
   * touch `isPaused`. Use `openInterface` / `closeInterface` from
   * `services/InterfaceService` to also handle pause behavior, or call
   * this directly when you need the freedom to decouple them (e.g. a
   * future non-pausing overlay).
   */
  setActiveInterface: (id: string | null) => void;
  setCurrentShopId: (id: string | null) => void;
  setCurrentDungeonId: (id: string | null) => void;
  setDungeonRunConfig: (c: DungeonRunConfig | null) => void;
  mutateDungeonRunConfig: (
    fn: (c: DungeonRunConfig) => DungeonRunConfig,
  ) => void;
  setSpellEquipPendingId: (id: string | null) => void;
}

export const useGameStore = create<GameState & GameActions>((set) => ({
  cameraPosition: { x: 0, y: 0 },
  screenWidth: 100,
  screenHeight: 100,
  zoomFactor: 5,
  playerPosition: { x: 10, y: 10 },
  lockCameraPosition: false,
  gameplay: 'main-menu',
  currentDelta: 1.0,
  isPaused: false,
  isCapturingInput: false,
  currentFloor: 1,
  activeInterface: null as string | null,
  currentShopId: null as string | null,
  currentDungeonId: null as string | null,
  dungeonRunConfig: null as DungeonRunConfig | null,
  spellEquipPendingId: null as string | null,

  setCameraPosition: (pos) => set({ cameraPosition: pos }),
  setScreenWidth: (w) => set({ screenWidth: w }),
  setScreenHeight: (h) => set({ screenHeight: h }),
  setZoomFactor: (z) => set({ zoomFactor: z }),
  setPlayerPosition: (pos) => set({ playerPosition: pos }),
  setLockCameraPosition: (v) => set({ lockCameraPosition: v }),
  setGameplay: (v) => set({ gameplay: v }),
  setCurrentDelta: (d) => set({ currentDelta: d }),
  setIsPaused: (v) => set({ isPaused: v }),
  toggleIsPaused: () => set((s) => ({ isPaused: !s.isPaused })),
  setIsCapturingInput: (v) => set({ isCapturingInput: v }),
  setCurrentFloor: (f) => set({ currentFloor: f }),
  setActiveInterface: (id) => set({ activeInterface: id }),
  setCurrentShopId: (id) => set({ currentShopId: id }),
  setCurrentDungeonId: (id) => set({ currentDungeonId: id }),
  setDungeonRunConfig: (c) => set({ dungeonRunConfig: c }),
  mutateDungeonRunConfig: (fn) =>
    set((s) => ({
      dungeonRunConfig: s.dungeonRunConfig ? fn(s.dungeonRunConfig) : null,
    })),
  setSpellEquipPendingId: (id) => set({ spellEquipPendingId: id }),
}));
